import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Users,
  FileText,
  BookOpen,
  Sparkles,
  ShieldCheck,
  BarChart3,
  Plus,
  Mail,
  RefreshCw,
  Search,
  ExternalLink,
  Cpu,
  Trash2,
  Clock,
  Target,
  Award,
  AlertCircle,
  ChevronLeft,
  Briefcase,
  TrendingUp,
  Link2,
  Copy,
  Check,
  Download,
  Loader2,
  MessageSquare,
  Pencil,
  Settings as SettingsIcon,
  BrainCircuit,
} from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import { client } from "@/src/api/client.gen";
import { EmployeeDossierDrawer } from "../components/EmployeeDossierDrawer";
import type {
  WorkspaceItem,
  WorkspaceDetail,
  WorkspaceDocumentItem,
  WorkspaceCourseItem,
  WorkspaceCustomCourseRequestItem,
  WorkspaceLearningRuleItem,
  WorkspaceSkillExpectationItem,
  WorkspaceAnalytics,
  MemberRow,
  OrgStats,
} from "../types";
import { getErrorMessage } from "../types";
import {
  AddWorkspaceMemberModal,
  InviteWorkspaceMemberModal,
  WorkspaceDocumentUploadModal,
  LinkCourseToWorkspaceModal,
  CustomCourseRequestModal,
  AddLearningRuleModal,
  AddSkillBenchmarkModal,
  CreateWorkspaceModal,
  ConfirmActionModal,
  type ConfirmAction,
} from "../components/modals";

export interface WorkspaceDetailPageProps {
  slug: string;
  workspace: WorkspaceItem;
  detailedWorkspace: WorkspaceDetail | null;
  loadingWorkspaceDetail: boolean;
  members: MemberRow[];
  stats: OrgStats;
  onBack?: () => void;
  loadWorkspaceDetail: (wsSlug: string, force?: boolean) => Promise<void>;
  loadWorkspaces: (force?: boolean) => Promise<void>;
  loadMembers: (force?: boolean) => Promise<void>;
}

export const WorkspaceDetailPage: React.FC<WorkspaceDetailPageProps> = ({
  slug,
  workspace,
  detailedWorkspace,
  loadingWorkspaceDetail,
  members,
  stats,
  onBack,
  loadWorkspaceDetail,
  loadWorkspaces,
  loadMembers,
}) => {
  const { user: clerkUser } = useUser();
  const [searchParams] = useSearchParams();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const [wsSubTab, setWsSubTab] = useState<
    "members" | "documents" | "courses" | "custom_courses" | "rules_skills" | "analytics" | "settings"
  >(() => {
    const initialSubTab = searchParams.get("subtab");
    if (
      initialSubTab &&
      ["members", "documents", "courses", "custom_courses", "rules_skills", "analytics", "settings"].includes(initialSubTab)
    ) {
      return initialSubTab as any;
    }
    return "members";
  });

  useEffect(() => {
    const st = searchParams.get("subtab");
    if (
      st &&
      ["members", "documents", "courses", "custom_courses", "rules_skills", "analytics", "settings"].includes(st)
    ) {
      setWsSubTab(st as any);
    }
  }, [searchParams]);

  // Employee Progress & Dossier Drawer State
  const [selectedDossierMember, setSelectedDossierMember] = useState<{
    user_id: number;
    name?: string | null;
    email?: string | null;
  } | null>(null);

  // Workspace Settings & Seat Licensing State
  const [wsSeatLimit, setWsSeatLimit] = useState<string>(
    detailedWorkspace?.seat_limit != null
      ? String(detailedWorkspace.seat_limit)
      : workspace.seat_limit != null
      ? String(workspace.seat_limit)
      : ""
  );
  const [wsMinPassingScore, setWsMinPassingScore] = useState<number>(
    detailedWorkspace?.settings?.min_passing_score ?? (workspace.settings?.min_passing_score ?? 75)
  );
  const [wsDueDaysDefault, setWsDueDaysDefault] = useState<number>(
    detailedWorkspace?.settings?.default_due_days ?? (workspace.settings?.default_due_days ?? 30)
  );
  const [wsAutoRemediation, setWsAutoRemediation] = useState<boolean>(
    detailedWorkspace?.settings?.auto_remediation_enabled ?? (workspace.settings?.auto_remediation_enabled ?? true)
  );
  const [wsNudgeOnOverdue, setWsNudgeOnOverdue] = useState<boolean>(
    detailedWorkspace?.settings?.nudge_on_overdue ?? (workspace.settings?.nudge_on_overdue ?? true)
  );
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaveMsg, setSettingsSaveMsg] = useState<{ text: string; type: "success" | "error" } | null>(null);

  useEffect(() => {
    if (detailedWorkspace) {
      setWsSeatLimit(detailedWorkspace.seat_limit != null ? String(detailedWorkspace.seat_limit) : "");
      if (detailedWorkspace.settings) {
        if (typeof detailedWorkspace.settings.min_passing_score === "number") {
          setWsMinPassingScore(detailedWorkspace.settings.min_passing_score);
        }
        if (typeof detailedWorkspace.settings.default_due_days === "number") {
          setWsDueDaysDefault(detailedWorkspace.settings.default_due_days);
        }
        if (typeof detailedWorkspace.settings.auto_remediation_enabled === "boolean") {
          setWsAutoRemediation(detailedWorkspace.settings.auto_remediation_enabled);
        }
        if (typeof detailedWorkspace.settings.nudge_on_overdue === "boolean") {
          setWsNudgeOnOverdue(detailedWorkspace.settings.nudge_on_overdue);
        }
      }
    }
  }, [detailedWorkspace]);

  const handleSaveWorkspaceSettings = async () => {
    setSavingSettings(true);
    setSettingsSaveMsg(null);
    try {
      const seatVal = wsSeatLimit.trim() ? parseInt(wsSeatLimit.trim(), 10) : null;
      await client.patch({
        url: `/api/v1/b2b/${slug}/workspaces/${workspace.slug}/settings`,
        body: {
          seat_limit: seatVal,
          settings: {
            min_passing_score: wsMinPassingScore,
            default_due_days: wsDueDaysDefault,
            auto_remediation_enabled: wsAutoRemediation,
            nudge_on_overdue: wsNudgeOnOverdue,
          },
        },
      });
      setSettingsSaveMsg({ text: "Workspace settings and seat license quota saved successfully.", type: "success" });
      await loadWorkspaceDetail(workspace.slug, true);
      await loadWorkspaces(true);
    } catch (err: any) {
      setSettingsSaveMsg({ text: getErrorMessage(err, "Failed to save workspace settings"), type: "error" });
    } finally {
      setSavingSettings(false);
    }
  };

  // Subtab Data States
  const [wsDocuments, setWsDocuments] = useState<WorkspaceDocumentItem[]>([]);
  const [loadingWsDocs, setLoadingWsDocs] = useState(false);
  const [vectorizingWsDocId, setVectorizingWsDocId] = useState<number | null>(null);
  const [wsRagQuery, setWsRagQuery] = useState("");
  const [searchingWsRag, setSearchingWsRag] = useState(false);
  const [wsRagResults, setWsRagResults] = useState<any[]>([]);

  const [wsCourses, setWsCourses] = useState<WorkspaceCourseItem[]>([]);
  const [loadingWsCourses, setLoadingWsCourses] = useState(false);

  const [wsCustomRequests, setWsCustomRequests] = useState<WorkspaceCustomCourseRequestItem[]>([]);
  const [loadingCustomRequests, setLoadingCustomRequests] = useState(false);
  const [editingCustomRequest, setEditingCustomRequest] = useState<WorkspaceCustomCourseRequestItem | null>(null);

  const [wsRules, setWsRules] = useState<WorkspaceLearningRuleItem[]>([]);
  const [loadingWsRules, setLoadingWsRules] = useState(false);

  const [wsSkills, setWsSkills] = useState<WorkspaceSkillExpectationItem[]>([]);
  const [loadingWsSkills, setLoadingWsSkills] = useState(false);

  const [wsAnalytics, setWsAnalytics] = useState<WorkspaceAnalytics | null>(null);
  const [loadingWsAnalytics, setLoadingWsAnalytics] = useState(false);

  // Modal Visibility States
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showInviteMemberModal, setShowInviteMemberModal] = useState(false);
  const [showWsDocModal, setShowWsDocModal] = useState(false);
  const [showLinkCourseModal, setShowLinkCourseModal] = useState(false);
  const [showCustomRequestModal, setShowCustomRequestModal] = useState(false);
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [showAddSkillModal, setShowAddSkillModal] = useState(false);
  const [showCreateWsModal, setShowCreateWsModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const navigate = useNavigate();

  const joinLinkUrl = useMemo(() => {
    if (!stats?.join_token) return "";
    return `${window.location.origin}/join/${slug}?token=${stats.join_token}`;
  }, [slug, stats?.join_token]);

  const handleCopyLink = () => {
    if (!joinLinkUrl) return;
    void navigator.clipboard.writeText(joinLinkUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const resolveDocUrl = (url: string) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const rawApiBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "";
    const cleanedBase = rawApiBase.replace(/\/api\/v1\/?$/, "");
    return `${cleanedBase}${url}`;
  };

  const [isExportingReport, setIsExportingReport] = useState(false);

  const handleExportCSV = async () => {
    if (!slug || isExportingReport) return;
    setIsExportingReport(true);
    try {
      const res = await api.exportEmployeeReportApiV1B2bSlugExportReportGet({
        path: { slug },
        parseAs: "blob",
      } as any);

      if (res.error || !res.data) {
        throw new Error("Failed to export organization report");
      }

      const blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([String(res.data)], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `${slug}_learning_report_${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export report:", err);
      alert("Could not export report. Please try again.");
    } finally {
      setIsExportingReport(false);
    }
  };

  // Load subtab data
  const loadWorkspaceDocuments = useCallback(async () => {
    if (!slug || !workspace.slug) return;
    try {
      setLoadingWsDocs(true);
      const { data } = await api.listWorkspaceDocumentsApiV1B2bSlugWorkspacesWsSlugDocumentsGet({
        path: { slug, ws_slug: workspace.slug },
      });
      if (data) {
        setWsDocuments((data as any).documents || []);
      }
    } catch (err) {
      console.error("Failed to load workspace documents:", err);
    } finally {
      setLoadingWsDocs(false);
    }
  }, [slug, workspace.slug]);

  const loadWorkspaceCourses = useCallback(async () => {
    if (!slug || !workspace.slug) return;
    try {
      setLoadingWsCourses(true);
      const { data } = await api.listWorkspaceCoursesApiV1B2bSlugWorkspacesWsSlugCoursesGet({
        path: { slug, ws_slug: workspace.slug },
      });
      if (data) {
        setWsCourses((data as any).courses || []);
      }
    } catch (err) {
      console.error("Failed to load workspace courses:", err);
    } finally {
      setLoadingWsCourses(false);
    }
  }, [slug, workspace.slug]);

  const loadWorkspaceCustomRequests = useCallback(async () => {
    if (!slug || !workspace.slug) return;
    try {
      setLoadingCustomRequests(true);
      const { data } =
        await api.listWorkspaceCustomCourseRequestsApiV1B2bSlugWorkspacesWsSlugCustomCourseRequestsGet({
          path: { slug, ws_slug: workspace.slug },
        });
      if (data) {
        setWsCustomRequests((data as any).requests || []);
      }
    } catch (err) {
      console.error("Failed to load custom requests:", err);
    } finally {
      setLoadingCustomRequests(false);
    }
  }, [slug, workspace.slug]);

  const loadWorkspaceRules = useCallback(async () => {
    if (!slug || !workspace.slug) return;
    try {
      setLoadingWsRules(true);
      const { data } = await api.listWorkspaceLearningRulesApiV1B2bSlugWorkspacesWsSlugRulesGet({
        path: { slug, ws_slug: workspace.slug },
      });
      if (data) {
        setWsRules((data as any).rules || []);
      }
    } catch (err) {
      console.error("Failed to load workspace rules:", err);
    } finally {
      setLoadingWsRules(false);
    }
  }, [slug, workspace.slug]);

  const loadWorkspaceSkills = useCallback(async () => {
    if (!slug || !workspace.slug) return;
    try {
      setLoadingWsSkills(true);
      const { data } = await api.listWorkspaceSkillExpectationsApiV1B2bSlugWorkspacesWsSlugSkillsGet({
        path: { slug, ws_slug: workspace.slug },
      });
      if (data) {
        setWsSkills((data as any).skills || []);
      }
    } catch (err) {
      console.error("Failed to load workspace skills:", err);
    } finally {
      setLoadingWsSkills(false);
    }
  }, [slug, workspace.slug]);

  const loadWorkspaceAnalytics = useCallback(async () => {
    if (!slug || !workspace.slug) return;
    try {
      setLoadingWsAnalytics(true);
      const { data } = await api.getWorkspaceAnalyticsApiV1B2bSlugWorkspacesWsSlugAnalyticsGet({
        path: { slug, ws_slug: workspace.slug },
      });
      if (data) {
        setWsAnalytics(data as any);
      }
    } catch (err) {
      console.error("Failed to load workspace analytics:", err);
    } finally {
      setLoadingWsAnalytics(false);
    }
  }, [slug, workspace.slug]);

  const loadedSubTabsRef = useRef<{ [key: string]: boolean }>({});

  // Load core workspace detail when workspace changes
  useEffect(() => {
    if (workspace.slug && detailedWorkspace?.slug !== workspace.slug) {
      void loadWorkspaceDetail(workspace.slug, true);
    }
  }, [workspace.slug, detailedWorkspace?.slug, loadWorkspaceDetail]);

  // Preload all workspace subtab data so tab count badges are accurate immediately
  useEffect(() => {
    if (!workspace.slug) return;
    loadedSubTabsRef.current = {
      documents: true,
      courses: true,
      custom_courses: true,
      rules: true,
      skills: true,
      analytics: true,
    };
    void Promise.all([
      loadWorkspaceDocuments(),
      loadWorkspaceCourses(),
      loadWorkspaceRules(),
      loadWorkspaceSkills(),
      loadWorkspaceCustomRequests(),
      loadWorkspaceAnalytics(),
    ]);
  }, [
    workspace.slug,
    loadWorkspaceDocuments,
    loadWorkspaceCourses,
    loadWorkspaceRules,
    loadWorkspaceSkills,
    loadWorkspaceCustomRequests,
    loadWorkspaceAnalytics,
  ]);

  // Lazy load analytics when analytics subtab is visited
  useEffect(() => {
    if (!workspace.slug) return;
    if (wsSubTab === "analytics" && !loadedSubTabsRef.current.analytics) {
      loadedSubTabsRef.current.analytics = true;
      void loadWorkspaceAnalytics();
    }
  }, [wsSubTab, workspace.slug, loadWorkspaceAnalytics]);

  // Handlers
  const handleRemoveMemberFromWorkspace = async (userId: number, userName?: string | null) => {
    setConfirmAction({
      title: "Remove workspace member?",
      description: `Remove ${userName || "this member"} from ${workspace.name}? Their membership in the organization will remain unchanged.`,
      confirmLabel: "Remove member",
      onConfirm: async () => {
        try {
          const { error } = await api.removeWorkspaceMemberApiV1B2bSlugWorkspacesWsSlugMembersUserIdDelete({
            path: { slug, ws_slug: workspace.slug, user_id: userId },
          });
          if (!error) {
            await loadWorkspaceDetail(workspace.slug, true);
            await loadWorkspaces(true);
            await loadMembers(true);
          } else {
            alert(`Failed to remove member: ${getErrorMessage(error, "Unknown error")}`);
          }
        } catch (err) {
          console.error("Error removing member:", err);
        }
      },
    });
  };

  const handleVectorizeWsDoc = async (docId: number) => {
    try {
      setVectorizingWsDocId(docId);
      const { data, error } =
        await api.vectorizeWorkspaceDocumentApiV1B2bSlugWorkspacesWsSlugDocumentsDocIdVectorizePost({
          path: { slug, ws_slug: workspace.slug, doc_id: docId },
        });
      if (data) {
        alert("Document vectorization task enqueued for workspace!");
        await loadWorkspaceDocuments();
      } else {
        alert(`Vectorization failed: ${getErrorMessage(error, "Unknown error")}`);
      }
    } catch (err) {
      console.error("Failed to vectorize workspace doc:", err);
    } finally {
      setVectorizingWsDocId(null);
    }
  };

  const handleSearchWsRAG = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wsRagQuery.trim()) return;
    try {
      setSearchingWsRag(true);
      const { data } = await api.searchWorkspaceKnowledgeBaseApiV1B2bSlugWorkspacesWsSlugKnowledgeBaseSearchPost({
        path: { slug, ws_slug: workspace.slug },
        body: { query: wsRagQuery.trim(), top_k: 4 },
      });
      if (data) {
        setWsRagResults((data as any).results || []);
      }
    } catch (err) {
      console.error("Failed to search workspace knowledge base:", err);
    } finally {
      setSearchingWsRag(false);
    }
  };

  const handleUnlinkCourseFromWs = async (courseId: number, courseTitle: string) => {
    setConfirmAction({
      title: "Unlink course from workspace?",
      description: `Remove ${courseTitle} from ${workspace.name}? Members will lose access to this course through this workspace.`,
      confirmLabel: "Unlink course",
      onConfirm: async () => {
        try {
          const { error } = await api.unlinkCourseFromWorkspaceApiV1B2bSlugWorkspacesWsSlugCoursesCourseIdDelete({
            path: { slug, ws_slug: workspace.slug, course_id: courseId },
          });
          if (!error) {
            await loadWorkspaceCourses();
            await loadWorkspaceRules();
          } else {
            alert(`Failed to unlink course: ${getErrorMessage(error, "Unknown error")}`);
          }
        } catch (err) {
          console.error("Error unlinking course:", err);
        }
      },
    });
  };

  const handleDeleteLearningRule = async (ruleId: number) => {
    setConfirmAction({
      title: "Remove learning rule?",
      description: `Remove this learning compliance rule from ${workspace.name}?`,
      confirmLabel: "Remove rule",
      onConfirm: async () => {
        try {
          const { error } = await api.deleteWorkspaceLearningRuleApiV1B2bSlugWorkspacesWsSlugRulesRuleIdDelete({
            path: { slug, ws_slug: workspace.slug, rule_id: ruleId },
          });
          if (!error) {
            await loadWorkspaceRules();
            await loadWorkspaceAnalytics();
          } else {
            alert(getErrorMessage(error, "Failed to delete rule"));
          }
        } catch (err) {
          console.error("Failed to delete rule:", err);
        }
      },
    });
  };

  const handleDeleteSkillBenchmark = async (skillId: number) => {
    setConfirmAction({
      title: "Delete skill expectation?",
      description: `Delete this skill expectation from ${workspace.name}?`,
      confirmLabel: "Delete expectation",
      onConfirm: async () => {
        try {
          const { error } = await api.deleteWorkspaceSkillExpectationApiV1B2bSlugWorkspacesWsSlugSkillsSkillIdDelete({
            path: { slug, ws_slug: workspace.slug, skill_id: skillId },
          });
          if (!error) {
            await loadWorkspaceSkills();
            await loadWorkspaceAnalytics();
          } else {
            alert(getErrorMessage(error, "Failed to delete skill benchmark"));
          }
        } catch (err) {
          console.error("Failed to delete skill benchmark:", err);
        }
      },
    });
  };

  const totalMembersCount = detailedWorkspace?.members?.length ?? workspace.member_count ?? 0;
  const seatLimit = stats?.seat_limit || 0;
  const seatPct = seatLimit > 0 ? Math.min(100, Math.round((totalMembersCount / seatLimit) * 100)) : 0;

  return (
    <div className="space-y-6">
      <ConfirmActionModal action={confirmAction} onClose={() => setConfirmAction(null)} />
      {/* 1. Breadcrumb & Navigation Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          {onBack ? (
            <button
              onClick={onBack}
              className="hover:text-indigo-600 flex items-center gap-1.5 transition cursor-pointer"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span>{stats.organization_name}</span>
              <span className="text-slate-300">/</span>
              <span className="text-slate-600">Workspaces</span>
            </button>
          ) : (
            <span>{stats.organization_name} / Workspaces</span>
          )}
          <span className="text-slate-300">/</span>
          <span className="text-slate-900 font-bold">{workspace.name}</span>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-center">
          <button
            onClick={handleExportCSV}
            disabled={isExportingReport}
            className="px-3.5 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-60 border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow-2xs transition cursor-pointer"
          >
            {isExportingReport ? (
              <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5 text-slate-400" />
            )}
            <span>{isExportingReport ? "Exporting..." : "Export"}</span>
          </button>
          <button
            onClick={() => navigate("/catalog")}
            className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow-2xs transition cursor-pointer"
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span>Catalog</span>
          </button>
        </div>
      </div>

      {/* 2. Workspace Overview & Action Header */}
      {/* <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl shadow-2xs flex-shrink-0">
            <Briefcase className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-slate-900 tracking-tight">{workspace.name}</h2>
              {workspace.is_default && (
                <span className="px-2 py-0.5 bg-slate-900 text-white text-[10px] font-bold uppercase rounded-md">
                  Default
                </span>
              )}
              <span className="text-xs font-bold text-blue-600 bg-blue-50 border border-blue-200/60 px-2 py-0.5 rounded-md">
                Active
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {totalMembersCount} {totalMembersCount === 1 ? "member" : "members"} · /{workspace.slug} · {workspace.description || "General team collaboration and learning workspace."}
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowCreateWsModal(true)}
          className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-xs flex items-center gap-2 transition shrink-0 active:scale-95 cursor-pointer self-start sm:self-center"
        >
          <Plus className="w-4 h-4" />
          Create Workspace
        </button>
      </div> */}

      {/* 3. Workspace Compliance & Learning Analytics (Header Analytics Cards) */}
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-emerald-600" />
            Workspace Compliance &amp; Learning Analytics
          </h4>
          <p className="text-xs text-slate-500 mt-0.5">
            Real-time tracking of learner compliance rate, quiz scores, and skill gaps across /{workspace.slug}.
          </p>
        </div>

        {loadingWsAnalytics ? (
          <div className="py-8 text-center text-slate-400 bg-white rounded-2xl border border-slate-200">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-1.5" />
            <span className="text-xs">Calculating workspace analytics...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 1. Compliance Rate */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
              <div className="flex items-center justify-between text-xs text-slate-500 font-semibold mb-1">
                <span>Compliance Rate</span>
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="text-2xl font-black text-slate-900">
                {wsAnalytics?.compliance_rate ?? 100}%
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                <div
                  className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(wsAnalytics?.compliance_rate ?? 100, 100)}%` }}
                />
              </div>
            </div>

            {/* 2. Avg Quiz Score */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
              <div className="flex items-center justify-between text-xs text-slate-500 font-semibold mb-1">
                <span>Avg Quiz Score</span>
                <Award className="w-4 h-4 text-amber-500" />
              </div>
              <div className="text-2xl font-black text-slate-900">
                {wsAnalytics?.avg_quiz_score ?? 0}%
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Across all workspace tests</p>
            </div>

            {/* 3. Active Learners */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
              <div className="flex items-center justify-between text-xs text-slate-500 font-semibold mb-1">
                <span>Active Learners</span>
                <Users className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="text-2xl font-black text-slate-900">
                {wsAnalytics?.active_learners ?? 0}
                <span className="text-xs font-normal text-slate-400 ml-1">
                  / {wsAnalytics?.total_members ?? totalMembersCount} members
                </span>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Engaged in courses</p>
            </div>

            {/* 4. Skill Gaps */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
              <div className="flex items-center justify-between text-xs text-slate-500 font-semibold mb-1">
                <span>Skill Gaps</span>
                <AlertCircle className="w-4 h-4 text-rose-500" />
              </div>
              <div className="text-2xl font-black text-rose-600">
                {wsAnalytics?.skills_with_gaps ?? 0}
                <span className="text-xs font-normal text-slate-400 ml-1">
                  / {wsAnalytics?.skills_tracked ?? wsSkills.length} tracked
                </span>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Below target proficiency</p>
            </div>
          </div>
        )}
      </div>

      {/* 5. Clean Horizontal Tabs in Bottom */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs space-y-6">
        <div className="border-b border-slate-200 flex items-center gap-6 sm:gap-8 text-xs sm:text-sm font-semibold overflow-x-auto no-scrollbar">
          <button
            onClick={() => setWsSubTab("members")}
            className={`pb-3 transition flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
              wsSubTab === "members"
                ? "text-blue-600 border-b-2 border-blue-600 font-bold"
                : "text-slate-500 hover:text-slate-800 border-b-2 border-transparent"
            }`}
          >
            Members
            <span className="text-[11px] px-1.5 py-0.2 bg-slate-100 rounded-full text-slate-600 font-normal">
              {detailedWorkspace?.members.length || 0}
            </span>
          </button>

          <button
            onClick={() => setWsSubTab("documents")}
            className={`pb-3 transition flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
              wsSubTab === "documents"
                ? "text-blue-600 border-b-2 border-blue-600 font-bold"
                : "text-slate-500 hover:text-slate-800 border-b-2 border-transparent"
            }`}
          >
            SOPs
            <span className="text-[11px] px-1.5 py-0.2 bg-slate-100 rounded-full text-slate-600 font-normal">
              {wsDocuments.length}
            </span>
          </button>

          <button
            onClick={() => setWsSubTab("courses")}
            className={`pb-3 transition flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
              wsSubTab === "courses"
                ? "text-blue-600 border-b-2 border-blue-600 font-bold"
                : "text-slate-500 hover:text-slate-800 border-b-2 border-transparent"
            }`}
          >
            Courses
            <span className="text-[11px] px-1.5 py-0.2 bg-slate-100 rounded-full text-slate-600 font-normal">
              {wsCourses.length}
            </span>
          </button>

          <button
            onClick={() => setWsSubTab("rules_skills")}
            className={`pb-3 transition flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
              wsSubTab === "rules_skills"
                ? "text-blue-600 border-b-2 border-blue-600 font-bold"
                : "text-slate-500 hover:text-slate-800 border-b-2 border-transparent"
            }`}
          >
            Rules
            <span className="text-[11px] px-1.5 py-0.2 bg-slate-100 rounded-full text-slate-600 font-normal">
              {wsRules.length + wsSkills.length}
            </span>
          </button>

          <button
            onClick={() => setWsSubTab("custom_courses")}
            className={`pb-3 transition flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
              wsSubTab === "custom_courses"
                ? "text-blue-600 border-b-2 border-blue-600 font-bold"
                : "text-slate-500 hover:text-slate-800 border-b-2 border-transparent"
            }`}
          >
            Custom Requests
            <span className="text-[11px] px-1.5 py-0.2 bg-slate-100 rounded-full text-slate-600 font-normal">
              {wsCustomRequests.length}
            </span>
          </button>

          <button
            onClick={() => setWsSubTab("settings")}
            className={`pb-3 transition flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
              wsSubTab === "settings"
                ? "text-blue-600 border-b-2 border-blue-600 font-bold"
                : "text-slate-500 hover:text-slate-800 border-b-2 border-transparent"
            }`}
          >
            <SettingsIcon className="w-3.5 h-3.5" />
            <span>Settings &amp; Licensing</span>
          </button>
        </div>

      {/* Sub-tab 1: Members */}
      {wsSubTab === "members" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Workspace Members</h4>
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-xs"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Member
              </button>
              <button
                onClick={() => setShowInviteMemberModal(true)}
                className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-xs active:scale-95"
              >
                <Mail className="w-3.5 h-3.5" />
                Invite New
              </button>
            </div>
          </div>

          {loadingWorkspaceDetail ? (
            <div className="py-12 flex flex-col items-center justify-center text-slate-400">
              <RefreshCw className="w-6 h-6 animate-spin mb-2" />
              <span className="text-xs">Loading workspace members...</span>
            </div>
          ) : !detailedWorkspace || detailedWorkspace.members.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 border border-slate-100 rounded-2xl">
              <Users className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <h4 className="text-sm font-bold text-slate-700">No members in this workspace yet</h4>
              <p className="text-xs text-slate-500 mt-1">
                Add existing members from your organization roster or send a direct workspace invitation.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-xs">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider text-[10px]">
                    <th className="py-3.5 px-4">Member</th>
                    <th className="py-3.5 px-4">Workspace Role</th>
                    <th className="py-3.5 px-4">Joined Date</th>
                    <th className="py-3.5 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {detailedWorkspace.members.map((wm) => {
                    const isCurrentUser = Boolean(
                      clerkUser &&
                        (wm.clerk_user_id === clerkUser.id ||
                          (!wm.clerk_user_id && wm.email === clerkUser.primaryEmailAddress?.emailAddress))
                    );
                    const resolvedName =
                      (wm.name && wm.name !== "Enterprise User" ? wm.name : null) ||
                      (isCurrentUser ? clerkUser?.fullName : null) ||
                      (wm.name ? wm.name : null) ||
                      (wm.email ? wm.email.split("@")[0] : null) ||
                      "Enterprise User";
                    const resolvedEmail =
                      wm.email ||
                      (isCurrentUser ? clerkUser?.primaryEmailAddress?.emailAddress : null);
                    const initial = (resolvedName || "U").charAt(0).toUpperCase();

                    return (
                      <tr key={wm.id} className="hover:bg-slate-50/50 transition">
                        <td className="py-3 px-4">
                          <div
                            onClick={() =>
                              setSelectedDossierMember({
                                user_id: wm.user_id,
                                name: resolvedName,
                                email: resolvedEmail,
                              })
                            }
                            className="flex items-center gap-3 cursor-pointer group"
                            title="Click to view Employee Progress & Competency Dossier"
                          >
                            <div className="w-8 h-8 rounded-full bg-indigo-50 group-hover:bg-indigo-100 border border-indigo-100 text-indigo-700 font-bold text-xs flex items-center justify-center shrink-0 transition">
                              {initial}
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900 group-hover:text-indigo-600 transition flex items-center gap-1.5">
                                <span>{resolvedName}</span>
                                <BrainCircuit className="w-3.5 h-3.5 text-indigo-500 opacity-0 group-hover:opacity-100 transition" />
                              </div>
                              <div className="text-[11px] text-slate-500 font-mono">{resolvedEmail || "—"}</div>
                            </div>
                          </div>
                        </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                            wm.role === "admin"
                              ? "bg-purple-50 border border-purple-200 text-purple-700"
                              : wm.role === "manager"
                              ? "bg-amber-50 border border-amber-200 text-amber-700"
                              : "bg-slate-100 border border-slate-200 text-slate-700"
                          }`}
                        >
                          {wm.role}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-500">
                        {new Date(wm.joined_at).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setSelectedDossierMember({ user_id: wm.user_id, name: wm.name, email: wm.email })}
                            className="px-2.5 py-1 text-indigo-600 hover:bg-indigo-50 border border-indigo-100 hover:border-indigo-200 rounded-lg text-xs font-semibold transition flex items-center gap-1"
                            title="View employee progress & competency dossier"
                          >
                            <BrainCircuit className="w-3.5 h-3.5 text-indigo-500" />
                            <span>Progress</span>
                          </button>
                          <button
                            onClick={() => void handleRemoveMemberFromWorkspace(wm.user_id, wm.name || wm.email)}
                            className="px-2.5 py-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200 rounded-lg text-xs font-semibold transition"
                            title="Remove from workspace"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Sub-tab 2: Documents & SOPs */}
      {wsSubTab === "documents" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-bold text-slate-900">Workspace Documents &amp; SOPs</h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Upload standard operating procedures and internal policies scoped specifically to {workspace.name}.
              </p>
            </div>
            <button
              onClick={() => setShowWsDocModal(true)}
              className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-xs self-start sm:self-auto"
            >
              <Plus className="w-3.5 h-3.5" />
              Upload SOP / Policy
            </button>
          </div>

          {/* RAG Search Box */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-3">
            <div className="flex items-center gap-2 text-indigo-700 text-xs font-bold uppercase tracking-wider">
              <Search className="w-3.5 h-3.5 text-indigo-600" />
              Workspace RAG Knowledge Base Search
            </div>
            <form onSubmit={handleSearchWsRAG} className="flex gap-2">
              <input
                type="text"
                placeholder={`Search ${workspace.name} SOPs and playbooks...`}
                value={wsRagQuery}
                onChange={(e) => setWsRagQuery(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 shadow-xs"
              />
              <button
                type="submit"
                disabled={searchingWsRag}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shrink-0 shadow-xs disabled:opacity-50"
              >
                {searchingWsRag ? "Searching..." : "Vector Search"}
              </button>
            </form>

            {wsRagResults.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                {wsRagResults.map((r: any, i: number) => (
                  <div key={i} className="p-3 bg-white border border-slate-200 rounded-xl text-xs shadow-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-indigo-700 truncate">{r.document_title}</span>
                      <span className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-mono rounded">
                        {Math.round((r.similarity_score ?? 0) * 100)}% match
                      </span>
                    </div>
                    <p className="text-slate-600 text-[11px] whitespace-pre-wrap leading-relaxed">{r.chunk_text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {loadingWsDocs ? (
            <div className="py-8 text-center text-slate-400">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-1.5" />
              <span className="text-xs">Loading workspace documents...</span>
            </div>
          ) : wsDocuments.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 border border-slate-100 rounded-2xl">
              <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <h4 className="text-sm font-bold text-slate-700">No workspace documents yet</h4>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                Upload your team’s playbooks, runbooks, and SOPs to provide context for AI lesson generation.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {wsDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="p-4 bg-white border border-slate-200 rounded-2xl flex flex-col justify-between gap-3 shadow-xs hover:border-slate-300 transition"
                >
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <span className="font-bold text-slate-900 text-sm truncate">{doc.title}</span>
                      <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-bold uppercase rounded-md shrink-0">
                        {doc.document_type}
                      </span>
                    </div>
                    {doc.description && (
                      <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{doc.description}</p>
                    )}
                  </div>

                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <a
                        href={resolveDocUrl(doc.file_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 hover:text-indigo-700 font-semibold inline-flex items-center gap-1"
                      >
                        View Link
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <button
                      onClick={() => handleVectorizeWsDoc(doc.id)}
                      disabled={vectorizingWsDocId === doc.id}
                      className="px-2.5 py-1 bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 text-[11px] font-semibold rounded-lg flex items-center gap-1 transition disabled:opacity-50"
                    >
                      <Cpu className="w-3 h-3" />
                      {vectorizingWsDocId === doc.id ? "Vectorizing..." : "Vectorize AI"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sub-tab 3: Courses */}
      {wsSubTab === "courses" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-bold text-slate-900">Assigned Catalog Tracks</h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Courses linked to this workspace can be assigned directly to team members with tracking.
              </p>
            </div>
            <button
              onClick={() => setShowLinkCourseModal(true)}
              className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-xs self-start sm:self-auto"
            >
              <Plus className="w-3.5 h-3.5" />
              Assign Catalog Course
            </button>
          </div>

          {loadingWsCourses ? (
            <div className="py-8 text-center text-slate-400">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-1.5" />
              <span className="text-xs">Loading assigned courses...</span>
            </div>
          ) : wsCourses.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 border border-slate-100 rounded-2xl">
              <BookOpen className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <h4 className="text-sm font-bold text-slate-700">No courses assigned to this workspace</h4>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                Link courses from your organization’s sponsored tracks to train members of this workspace.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {wsCourses.map((c) => (
                <div
                  key={c.id}
                  className="p-4 bg-white border border-slate-200 rounded-2xl flex flex-col justify-between shadow-xs hover:border-slate-300 transition"
                >
                  <div>
                    <h5 className="font-bold text-slate-900 text-sm truncate">{c.title}</h5>
                    {c.description && (
                      <p className="text-xs text-slate-500 line-clamp-2 mt-1 leading-relaxed">{c.description}</p>
                    )}
                  </div>
                  <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <span className="text-[11px] text-slate-400">
                      {c.assigned_at || c.linked_at
                        ? `Linked: ${new Date(c.assigned_at || c.linked_at!).toLocaleDateString()}`
                        : "Linked"}
                    </span>
                    <button
                      onClick={() => handleUnlinkCourseFromWs(c.id, c.title)}
                      className="text-slate-400 hover:text-rose-600 text-xs font-semibold transition"
                    >
                      Unlink
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sub-tab 4: Custom Course Requests */}
      {wsSubTab === "custom_courses" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-600" />
                Custom Course Generation Requests
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Generate AI curriculum customized to {workspace.name}&apos;s documents and SOPs.
              </p>
            </div>
            <button
              onClick={() => {
                setEditingCustomRequest(null);
                setShowCustomRequestModal(true);
              }}
              className="px-3.5 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-xs self-start sm:self-auto cursor-pointer"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Request Custom Course
            </button>
          </div>

          {loadingCustomRequests ? (
            <div className="py-8 text-center text-slate-400">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-1.5" />
              <span className="text-xs">Loading custom requests...</span>
            </div>
          ) : wsCustomRequests.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 border border-slate-100 rounded-2xl">
              <Sparkles className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <h4 className="text-sm font-bold text-slate-700">No custom course requests submitted yet</h4>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                Request a tailored training program for your workspace team, grounded in your uploaded SOPs.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {wsCustomRequests.map((req) => {
                const statusBadge =
                  req.status === "completed" || req.status === "published"
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : req.status === "generating"
                    ? "bg-purple-50 border-purple-200 text-purple-700 animate-pulse"
                    : req.status === "in_review"
                    ? "bg-amber-50 border-amber-200 text-amber-700"
                    : req.status === "rejected"
                    ? "bg-rose-50 border-rose-200 text-rose-700"
                    : "bg-slate-100 border-slate-200 text-slate-700";

                return (
                  <div
                    key={req.id}
                    className="p-4 bg-white border border-slate-200 rounded-2xl shadow-xs space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {req.image_url && (
                          <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-200 shrink-0 bg-slate-100 shadow-2xs">
                            <img
                              src={req.image_url}
                              alt={req.course_title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = "none";
                              }}
                            />
                          </div>
                        )}
                        <div className="min-w-0">
                          <h5 className="font-bold text-slate-900 text-sm truncate">{req.course_title}</h5>
                          <p className="text-xs text-slate-500 mt-0.5">Audience: {req.target_audience}</p>
                        </div>
                      </div>
                      <span className={`px-2.5 py-0.5 border text-[10px] font-bold uppercase rounded-md shrink-0 ${statusBadge}`}>
                        {req.status}
                      </span>
                    </div>

                    <p className="text-xs text-slate-600 leading-relaxed">{req.learning_objectives}</p>

                    {/* Admin Feedback Display */}
                    {req.admin_feedback && (
                      <div className="p-3 bg-amber-50/80 border border-amber-200 rounded-xl text-xs space-y-1">
                        <div className="flex items-center gap-1.5 font-bold text-amber-900">
                          <MessageSquare className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                          <span>Super Admin Feedback &amp; Notes:</span>
                        </div>
                        <p className="text-amber-800 leading-relaxed pl-5 whitespace-pre-wrap">
                          {req.admin_feedback}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <div className="text-[11px] text-slate-400">
                        Submitted on {new Date(req.created_at).toLocaleDateString()}
                      </div>
                      {req.status !== "generating" && req.status !== "published" && (
                        <button
                          onClick={() => {
                            setEditingCustomRequest(req);
                            setShowCustomRequestModal(true);
                          }}
                          className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition cursor-pointer"
                        >
                          <Pencil className="w-3 h-3" />
                          {req.admin_feedback ? "Review & Revise Request" : "Edit Request"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Sub-tab 5: Rules & Skills */}
      {wsSubTab === "rules_skills" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-bold text-slate-900">Learning Rules &amp; Skill Benchmarks</h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Configure mandatory course completion rules, SLA deadlines, passing thresholds, and skill proficiencies.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddRuleModal(true)}
                className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-xs"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Rule
              </button>
              <button
                onClick={() => setShowAddSkillModal(true)}
                className="px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-xs"
              >
                <Target className="w-3.5 h-3.5" />
                Add Skill Benchmark
              </button>
            </div>
          </div>

          {/* Rules Section */}
          <div className="space-y-3">
            <h5 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5 text-indigo-600" />
              Course Compliance Rules ({wsRules.length})
            </h5>

            {loadingWsRules ? (
              <div className="py-8 text-center text-slate-400">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-1.5" />
                <span className="text-xs">Loading compliance rules...</span>
              </div>
            ) : wsRules.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 border border-slate-100 rounded-2xl">
                <ShieldCheck className="w-7 h-7 text-slate-400 mx-auto mb-2" />
                <h4 className="text-xs font-bold text-slate-700">No compliance rules configured</h4>
                <p className="text-[11px] text-slate-500 mt-1 max-w-sm mx-auto">
                  Set passing thresholds, SLA completion deadlines, or AI remediation triggers for courses.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-2xl bg-white shadow-xs">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                      <th className="py-3 px-4">Course</th>
                      <th className="py-3 px-4">Requirement</th>
                      <th className="py-3 px-4">Passing Score</th>
                      <th className="py-3 px-4">SLA Deadline</th>
                      <th className="py-3 px-4">Remediation</th>
                      <th className="py-3 px-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {wsRules.map((rule) => (
                      <tr key={rule.id} className="hover:bg-slate-50/60 transition">
                        <td className="py-3 px-4 font-bold text-slate-900">
                          {rule.course_title || `Course #${rule.course_id}`}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${
                              rule.is_mandatory
                                ? "bg-rose-50 border-rose-200 text-rose-700"
                                : "bg-slate-100 border-slate-200 text-slate-600"
                            }`}
                          >
                            {rule.is_mandatory ? "Mandatory" : "Optional"}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-bold text-slate-800">{rule.passing_threshold_score}%</td>
                        <td className="py-3 px-4 text-slate-600">
                          {rule.due_days_after_enrollment ? (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5 text-amber-500" />
                              {rule.due_days_after_enrollment} days
                            </span>
                          ) : (
                            <span className="text-slate-400 italic">Self-paced</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {rule.remediation_required ? (
                            <span className="px-2 py-0.5 bg-purple-50 border border-purple-200 text-purple-700 rounded-md text-[10px] font-bold flex items-center gap-1 w-max">
                              <Sparkles className="w-3 h-3 text-purple-600" /> AI Guided
                            </span>
                          ) : (
                            <span className="text-slate-400 text-[11px]">Standard</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => handleDeleteLearningRule(rule.id)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                            title="Remove Rule"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Skills Section */}
          <div className="space-y-3 pt-4 border-t border-slate-100">
            <h5 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5 text-purple-600" />
              Skill Expectations &amp; Benchmarks ({wsSkills.length})
            </h5>

            {loadingWsSkills ? (
              <div className="py-8 text-center text-slate-400">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-1.5" />
                <span className="text-xs">Loading skill benchmarks...</span>
              </div>
            ) : wsSkills.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 border border-slate-100 rounded-2xl">
                <Target className="w-7 h-7 text-slate-400 mx-auto mb-2" />
                <h4 className="text-xs font-bold text-slate-700">No skill benchmarks defined</h4>
                <p className="text-[11px] text-slate-500 mt-1 max-w-sm mx-auto">
                  Define the target proficiencies expected for members in this workspace.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {wsSkills.map((sk) => {
                  const isWorkspaceScoped = sk.workspace_id === workspace.id;
                  const profColor =
                    sk.target_proficiency === "Expert"
                      ? "bg-purple-50 border-purple-200 text-purple-700"
                      : sk.target_proficiency === "Advanced"
                      ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                      : sk.target_proficiency === "Intermediate"
                      ? "bg-blue-50 border-blue-200 text-blue-700"
                      : "bg-slate-100 border-slate-200 text-slate-700";

                  return (
                    <div
                      key={sk.id}
                      className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs flex items-start justify-between gap-3"
                    >
                      <div className="space-y-1.5">
                        <div className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                          <Award className="w-4 h-4 text-amber-500 shrink-0" />
                          <span>{sk.skill_name}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${profColor}`}>
                            {sk.target_proficiency}
                          </span>
                          <span
                            className={`px-1.5 py-0.5 rounded-md text-[9px] font-semibold border ${
                              isWorkspaceScoped
                                ? "bg-indigo-50/80 border-indigo-200 text-indigo-700"
                                : "bg-slate-50 border-slate-200 text-slate-500"
                            }`}
                          >
                            {isWorkspaceScoped ? "Workspace" : "Org-Wide"}
                          </span>
                        </div>
                      </div>
                      {isWorkspaceScoped && (
                        <button
                          onClick={() => handleDeleteSkillBenchmark(sk.id)}
                          className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition shrink-0"
                          title="Delete Benchmark"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sub-tab 6: Analytics */}
      {wsSubTab === "analytics" && (
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-600" />
              Workspace Compliance &amp; Learning Analytics
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Real-time tracking of learner compliance rate, quiz scores, and skill gaps across /{workspace.slug}.
            </p>
          </div>

          {loadingWsAnalytics ? (
            <div className="py-12 text-center text-slate-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              <span className="text-xs">Calculating workspace analytics...</span>
            </div>
          ) : !wsAnalytics ? (
            <div className="text-center py-12 bg-slate-50 border border-slate-100 rounded-2xl">
              <BarChart3 className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <h4 className="text-sm font-bold text-slate-700">No analytics data available yet</h4>
            </div>
          ) : (
            <>
              {/* KPI Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-semibold mb-1">
                    <span>Compliance Rate</span>
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="text-2xl font-black text-slate-900">{wsAnalytics.compliance_rate}%</div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                    <div
                      className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(wsAnalytics.compliance_rate, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-semibold mb-1">
                    <span>Avg Quiz Score</span>
                    <Award className="w-4 h-4 text-amber-500" />
                  </div>
                  <div className="text-2xl font-black text-slate-900">{wsAnalytics.avg_quiz_score}%</div>
                  <p className="text-[10px] text-slate-400 mt-1">Across all workspace tests</p>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-semibold mb-1">
                    <span>Active Learners</span>
                    <Users className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="text-2xl font-black text-slate-900">
                    {wsAnalytics.active_learners}
                    <span className="text-xs font-normal text-slate-400 ml-1">
                      / {wsAnalytics.total_members} members
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">Engaged in courses</p>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-semibold mb-1">
                    <span>Skill Gaps</span>
                    <AlertCircle className="w-4 h-4 text-rose-500" />
                  </div>
                  <div className="text-2xl font-black text-rose-600">
                    {wsAnalytics.skills_with_gaps}
                    <span className="text-xs font-normal text-slate-400 ml-1">
                      / {wsAnalytics.skills_tracked} tracked
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">Below target proficiency</p>
                </div>
              </div>

              {/* Course Breakdown Table */}
              <div className="space-y-3 pt-2">
                <h5 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Course Compliance Breakdown
                </h5>

                {wsAnalytics.courses_breakdown.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 border border-slate-100 rounded-2xl text-xs text-slate-500">
                    No courses currently assigned or tracked for this workspace. Link courses from the &quot;Courses&quot; tab to see completion rates.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-2xl bg-white shadow-xs">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                          <th className="py-3 px-4">Course</th>
                          <th className="py-3 px-4">Requirement</th>
                          <th className="py-3 px-4">Assigned</th>
                          <th className="py-3 px-4">Completed</th>
                          <th className="py-3 px-4">Avg Score</th>
                          <th className="py-3 px-4">Compliance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-xs">
                        {wsAnalytics.courses_breakdown.map((item: any) => (
                          <tr key={item.course_id} className="hover:bg-slate-50/60 transition">
                            <td className="py-3 px-4 font-bold text-slate-900">{item.course_title}</td>
                            <td className="py-3 px-4">
                              <span
                                className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${
                                  item.is_mandatory
                                    ? "bg-rose-50 border-rose-200 text-rose-700"
                                    : "bg-slate-100 border-slate-200 text-slate-600"
                                }`}
                              >
                                {item.is_mandatory ? "Mandatory" : "Optional"}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-600 font-medium">
                              {item.total_assigned_learners}
                            </td>
                            <td className="py-3 px-4 text-slate-600 font-medium">{item.completed_learners}</td>
                            <td className="py-3 px-4 font-bold text-slate-800">{item.avg_score}%</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div className="w-20 bg-slate-100 rounded-full h-2 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      item.compliance_rate >= 80
                                        ? "bg-emerald-500"
                                        : item.compliance_rate >= 50
                                        ? "bg-amber-500"
                                        : "bg-rose-500"
                                    }`}
                                    style={{ width: `${Math.min(item.compliance_rate, 100)}%` }}
                                  />
                                </div>
                                <span className="font-bold text-slate-700 text-[11px]">
                                  {item.compliance_rate}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Sub-tab 7: Workspace Settings & Seat Licensing */}
      {wsSubTab === "settings" && (
        <div className="space-y-6 max-w-4xl">
          <div>
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <SettingsIcon className="w-4 h-4 text-indigo-600" />
              Workspace Settings &amp; Seat Licensing
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Configure dedicated seat allocation, skill benchmarks, and AI engine defaults for /{workspace.slug}.
            </p>
          </div>

          {settingsSaveMsg && (
            <div
              className={`p-4 rounded-xl text-xs font-semibold border flex items-center justify-between ${
                settingsSaveMsg.type === "success"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-rose-50 border-rose-200 text-rose-800"
              }`}
            >
              <span>{settingsSaveMsg.text}</span>
              <button
                type="button"
                onClick={() => setSettingsSaveMsg(null)}
                className="text-xs hover:opacity-75"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Seat Licensing Section */}
          <div className="p-6 bg-slate-50/70 border border-slate-200 rounded-2xl space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h5 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Seat Licensing &amp; Member Quota
                </h5>
                <p className="text-xs text-slate-500 mt-0.5">
                  Partition your organization&apos;s seat pool specifically for this workspace.
                </p>
              </div>
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                {wsSeatLimit ? `${wsSeatLimit} Seats Cap` : "Shared Org Pool"}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Dedicated Seat Limit
                </label>
                <input
                  type="number"
                  min={1}
                  placeholder="e.g. 25 (leave empty for unlimited)"
                  value={wsSeatLimit}
                  onChange={(e) => setWsSeatLimit(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-500"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Leave blank to allow workspace members up to overall organization seat limit.
                </p>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200 flex flex-col justify-between">
                <div>
                  <span className="text-[11px] font-semibold text-slate-400 block uppercase">
                    Active Utilization
                  </span>
                  <div className="text-lg font-black text-slate-900 mt-0.5">
                    {detailedWorkspace?.member_count ?? workspace.member_count} Members Active
                  </div>
                </div>
                {wsSeatLimit && parseInt(wsSeatLimit, 10) > 0 && (
                  <div className="w-full bg-slate-100 rounded-full h-2 mt-2 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        (detailedWorkspace?.member_count ?? workspace.member_count) >= parseInt(wsSeatLimit, 10)
                          ? "bg-rose-500"
                          : "bg-indigo-600"
                      }`}
                      style={{
                        width: `${Math.min(
                          100,
                          Math.round(
                            ((detailedWorkspace?.member_count ?? workspace.member_count) /
                              parseInt(wsSeatLimit, 10)) *
                              100
                          )
                        )}%`,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Skill Expectations & Learning Defaults */}
          <div className="p-6 bg-slate-50/70 border border-slate-200 rounded-2xl space-y-4">
            <div>
              <h5 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Skill Expectations &amp; Quiz Thresholds
              </h5>
              <p className="text-xs text-slate-500 mt-0.5">
                Baseline passing requirements applied to modules assigned within this workspace.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Default Minimum Passing Score (%)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min={50}
                    max={100}
                    value={wsMinPassingScore}
                    onChange={(e) => setWsMinPassingScore(Number(e.target.value))}
                    className="w-24 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-500 font-bold"
                  />
                  <div className="flex space-x-1">
                    {[70, 75, 80, 85].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setWsMinPassingScore(preset)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                          wsMinPassingScore === preset
                            ? "bg-indigo-600 text-white"
                            : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {preset}%
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Scores below this trigger automatic AI remediation micro-lessons.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Default Due Days After Enrollment
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={wsDueDaysDefault}
                    onChange={(e) => setWsDueDaysDefault(Number(e.target.value))}
                    className="w-24 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-500 font-bold"
                  />
                  <div className="flex space-x-1">
                    {[14, 30, 60, 90].map((days) => (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setWsDueDaysDefault(days)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                          wsDueDaysDefault === days
                            ? "bg-indigo-600 text-white"
                            : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {days}d
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Default timeline applied when assigning courses to workspace members.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wsAutoRemediation}
                  onChange={(e) => setWsAutoRemediation(e.target.checked)}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-xs font-bold text-slate-900 block">
                    Automated AI Remediation Engine
                  </span>
                  <span className="text-[11px] text-slate-500">
                    Automatically generate targeted AI remedial lessons whenever an employee fails a quiz question.
                  </span>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wsNudgeOnOverdue}
                  onChange={(e) => setWsNudgeOnOverdue(e.target.checked)}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-xs font-bold text-slate-900 block">
                    Manager Overdue Alerts &amp; Interventions
                  </span>
                  <span className="text-[11px] text-slate-500">
                    Flag learners as &ldquo;At Risk&rdquo; on the organization members roster when assignments approach due dates.
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              disabled={savingSettings}
              onClick={handleSaveWorkspaceSettings}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm transition disabled:opacity-50 flex items-center space-x-2"
            >
              {savingSettings ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Saving Changes...</span>
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>Save Workspace Settings</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
      </div>

      {/* Modals for Workspace */}
      <AddWorkspaceMemberModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        slug={slug}
        workspace={workspace}
        detailedWorkspace={detailedWorkspace}
        members={members}
        onSuccess={async () => {
          await loadWorkspaceDetail(workspace.slug, true);
          await loadMembers(true);
          await loadWorkspaces(true);
        }}
      />

      <InviteWorkspaceMemberModal
        isOpen={showInviteMemberModal}
        onClose={() => setShowInviteMemberModal(false)}
        slug={slug}
        workspace={workspace}
        onSuccess={async () => {
          await loadMembers(true);
          await loadWorkspaces(true);
          await loadWorkspaceDetail(workspace.slug, true);
        }}
      />

      <WorkspaceDocumentUploadModal
        isOpen={showWsDocModal}
        onClose={() => setShowWsDocModal(false)}
        slug={slug}
        workspace={workspace}
        onSuccess={loadWorkspaceDocuments}
      />

      <LinkCourseToWorkspaceModal
        isOpen={showLinkCourseModal}
        onClose={() => setShowLinkCourseModal(false)}
        slug={slug}
        workspace={workspace}
        assignedCourseIds={wsCourses.map((c) => c.id)}
        sponsoredCourses={stats?.sponsored_courses || []}
        onSuccess={async () => {
          await loadWorkspaceCourses();
          await loadWorkspaceRules();
        }}
      />

      <CustomCourseRequestModal
        isOpen={showCustomRequestModal}
        onClose={() => {
          setShowCustomRequestModal(false);
          setEditingCustomRequest(null);
        }}
        slug={slug}
        workspace={workspace}
        workspaceDocuments={wsDocuments}
        onSuccess={loadWorkspaceCustomRequests}
        initialData={editingCustomRequest}
      />

      <AddLearningRuleModal
        isOpen={showAddRuleModal}
        onClose={() => setShowAddRuleModal(false)}
        slug={slug}
        workspace={workspace}
        workspaceCourses={wsCourses}
        onSuccess={async () => {
          await loadWorkspaceRules();
          await loadWorkspaceAnalytics();
        }}
        onNavigateToCourses={() => {
          setShowAddRuleModal(false);
          setWsSubTab("courses");
        }}
      />

      <AddSkillBenchmarkModal
        isOpen={showAddSkillModal}
        onClose={() => setShowAddSkillModal(false)}
        slug={slug}
        workspace={workspace}
        onSuccess={async () => {
          await loadWorkspaceSkills();
          await loadWorkspaceAnalytics();
        }}
      />

      <CreateWorkspaceModal
        isOpen={showCreateWsModal}
        onClose={() => setShowCreateWsModal(false)}
        slug={slug}
        onSuccess={async (newWs) => {
          await loadWorkspaces(true);
          if (onBack) onBack();
        }}
      />

      {/* Employee Progress & AI Briefing Drawer */}
      <EmployeeDossierDrawer
        isOpen={!!selectedDossierMember}
        onClose={() => setSelectedDossierMember(null)}
        orgSlug={slug}
        userId={selectedDossierMember?.user_id || null}
        memberName={selectedDossierMember?.name}
        memberEmail={selectedDossierMember?.email}
      />
    </div>
  );
};
